import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { TrendingUp, Loader2, RefreshCw, Clock, Database, Play, Lock, FileText, ArrowUp, ArrowDown, Boxes } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import ScanRepricer from "@/components/ScanRepricer";
import { PillSwitch } from "@/components/PillSwitch";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const label = (h) => `${String(h).padStart(2, "0")}:00`;
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function Reprice() {
  const qc = useQueryClient();
  const [settings, setSettings] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [times, setTimes] = useState([]);
  const [roundUp, setRoundUp] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [healing, setHealing] = useState(false);
  const [boxed, setBoxed] = useState(null);
  const [boxedSyncing, setBoxedSyncing] = useState(false);
  const [report, setReport] = useState(null);

  const load = async () => {
    const [{ data: s }, { data: c }] = await Promise.all([
      api.get("/reprice/settings"),
      api.get("/catalog/status"),
    ]);
    setSettings(s); setEnabled(s.reprice_enabled); setTimes(s.reprice_times || []);
    setRoundUp(s.price_round_up || false);
    setCatalog(c); setReport(s.reprice_last_report || null);
    try { const { data: b } = await api.get("/boxed/status"); setBoxed(b); } catch { /* boxed add-on not enabled */ }
  };
  useEffect(() => { load(); }, []);

  const toggleTime = (h) => {
    setTimes((prev) => {
      if (prev.includes(h)) return prev.filter((x) => x !== h);
      const next = prev.length >= 2 ? [prev[prev.length - 1], h] : [...prev, h];
      return [...next].sort((a, b) => a - b);
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/reprice/settings", {
        reprice_enabled: enabled, reprice_times: times, price_round_up: roundUp,
      });
      toast.success("Repricing schedule saved");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/reprice/run");
      setReport(data);
      const sh = data.shopify_updated ? ` · ${data.shopify_updated} Shopify prices` : "";
      toast.success(`Repriced ${data.updated} of ${data.tracked} tracked cards${sh}`);
      qc.invalidateQueries({ queryKey: ["cards"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const resync = async () => {
    setSyncing(true);
    try {
      await api.post("/catalog/sync");
      toast.success("Catalog refresh started — this runs in the background");
      setTimeout(load, 2000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const healMissing = async () => {
    setHealing(true);
    try {
      const { data } = await api.post("/catalog/resync-missing");
      toast.success(data.groups ? `Healed ${data.groups} set(s), added ${data.added.toLocaleString()} cards` : "No missing sets found — catalog looks complete");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setHealing(false); }
  };

  const syncBoxed = async () => {
    setBoxedSyncing(true);
    try {
      const { data } = await api.post("/boxed/sync");
      if (data.started) {
        toast.success("Boxed buylist sync started — refreshing in the background");
        for (let i = 0; i < 8; i++) {
          await new Promise((r) => setTimeout(r, 3000));
          try { const { data: b } = await api.get("/boxed/status"); setBoxed(b); if (!b.running) break; } catch { /* ignore */ }
        }
      } else {
        toast.message(data.message || "A Boxed sync is already running");
        try { const { data: b } = await api.get("/boxed/status"); setBoxed(b); } catch { /* ignore */ }
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBoxedSyncing(false); }
  };

  const pct = catalog?.total_groups ? Math.round((catalog.done_groups / catalog.total_groups) * 100) : 0;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-100">Repricing</h1>
        <p className="text-sm text-slate-400 mt-1">Near Mint matches the live TCGPlayer market price; other conditions use the median-with-shipping listing price (fallback: lowest listing) via tcgapi.dev. Runs up to twice a day.</p>
      </div>

      <ScanRepricer />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Schedule */}
        <div className="lg:col-span-2 rounded-2xl border p-6" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center"><TrendingUp className="h-5 w-5 text-amber-400" /></div>
              <div>
                <h2 className="font-heading text-lg font-bold text-slate-100">Auto-reprice schedule</h2>
                <p className="text-xs text-slate-500">Times are in UTC · max 2 per day</p>
              </div>
            </div>
            <PillSwitch checked={enabled} onChange={setEnabled} color="emerald" testid="reprice-enable-toggle" />
          </div>

          <div className={`transition-opacity ${enabled ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3"><Clock className="h-3.5 w-3.5" /> Pick up to two times</div>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {HOURS.map((h) => (
                <button key={h} onClick={() => toggleTime(h)} data-testid={`reprice-hour-${h}`}
                  className={`py-2 rounded-lg text-xs font-mono-num border transition-colors ${times.includes(h) ? "bg-amber-500 text-[#1a1205] border-amber-500 font-semibold" : "text-slate-400 border-[var(--pk-border)] hover:bg-[var(--pk-hover)]"}`}>
                  {label(h)}
                </button>
              ))}
            </div>
            {times.length > 0 && (
              <p className="text-xs text-slate-400 mt-3">Scheduled at {times.map(label).join(" and ")} UTC daily.</p>
            )}
          </div>

          <div className="mt-6 pt-6 border-t flex items-start justify-between gap-4" style={{ borderColor: "var(--pk-border)" }} data-testid="reprice-roundup-row">
            <div>
              <h3 className="font-heading font-bold text-slate-100">Round up to the next dollar</h3>
              <p className="text-xs text-slate-500 mt-0.5 max-w-md">When on, every repriced card is rounded up to the next whole dollar (e.g. $1.01–$1.99 → $2.00). Applies to both TCG and Shopify prices.</p>
            </div>
            <PillSwitch checked={roundUp} onChange={setRoundUp} color="emerald" testid="reprice-roundup-toggle" />
          </div>

          <div className="flex gap-3 mt-6">
            <button onClick={save} disabled={saving} data-testid="reprice-save-button" className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Save Schedule
            </button>
            <button onClick={runNow} disabled={running} data-testid="reprice-run-now-button" className="flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold text-slate-100 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}>
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Reprice Now
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-4 leading-relaxed">
            <span className="text-slate-300">Near Mint</span> cards are repriced to the live <span className="text-slate-300">TCGPlayer market price</span>; <span className="text-slate-300">LP, MP, HP and DMG</span> use the median-with-shipping listing price (fallback: lowest listing) for that exact condition and printing via tcgapi.dev. Cards with a <span className="text-slate-300">manual price override</span> are skipped, cards with no data for their condition are left unchanged. Your <span className="text-slate-300">Shopify price always mirrors the TCG price</span> and is pushed to opted-in linked cards automatically.
          </p>
        </div>

        {/* Stats + catalog */}
        <div className="space-y-6">
          <div className="rounded-2xl border p-5" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4">Pricing coverage</div>
            <Row label="Tracked cards" value={settings?.tracked_cards ?? 0} color="text-cyan-400" />
            <Row label="Manual override" value={settings?.overridden_cards ?? 0} color="text-amber-400" icon={Lock} />
            <div className="mt-3 pt-3 border-t text-xs text-slate-500" style={{ borderColor: "var(--pk-border)" }}>
              Last run: <span className="text-slate-300">{settings?.reprice_last_run ? new Date(settings.reprice_last_run).toLocaleString() : "never"}</span>
            </div>
          </div>

          <div className="rounded-2xl border p-5" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3"><Database className="h-3.5 w-3.5" /> TCGPlayer catalog</div>
            <div className="text-2xl font-bold font-mono-num text-slate-100">{(catalog?.count ?? 0).toLocaleString()}</div>
            <div className="text-xs text-slate-500">products indexed</div>
            {catalog?.running && (
              <div className="mt-3">
                <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-cyan-500 transition-all" style={{ width: `${pct}%` }} /></div>
                <div className="text-[11px] text-slate-500 mt-1">Syncing {catalog.done_groups}/{catalog.total_groups} sets…</div>
              </div>
            )}
            <button onClick={resync} disabled={syncing || catalog?.running} data-testid="catalog-resync-button" className="mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-lg border text-xs text-slate-300 hover:bg-[var(--pk-hover)] disabled:opacity-50" style={{ borderColor: "var(--pk-border)" }}>
              {(syncing || catalog?.running) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Refresh catalog & prices
            </button>
            <button onClick={healMissing} disabled={healing || catalog?.running} data-testid="catalog-heal-button" className="mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-lg border text-xs text-slate-300 hover:bg-[var(--pk-hover)] disabled:opacity-50" style={{ borderColor: "var(--pk-border)" }}>
              {healing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Database className="h-3.5 w-3.5" />} Heal missing sets
            </button>
            <p className="text-[11px] text-slate-600 mt-1.5">Use "Heal missing sets" if some sets (e.g. Japanese Vending Series) are missing after a sync — it only re-fetches sets that came back empty.</p>
            {catalog?.last_synced && <div className="text-[11px] text-slate-600 mt-2">Updated {new Date(catalog.last_synced).toLocaleDateString()}</div>}
          </div>

          <div className="rounded-2xl border p-5" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3"><Boxes className="h-3.5 w-3.5" /> Boxed.gg buylist</div>
            <div className="text-2xl font-bold font-mono-num text-slate-100">{(boxed?.count ?? 0).toLocaleString()}</div>
            <div className="text-xs text-slate-500">cards Boxed is currently buying</div>
            {boxed?.running && (
              <div className="mt-3 text-[11px] text-cyan-400 flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Syncing buylist…</div>
            )}
            {boxed?.error && !boxed?.running && <div className="mt-2 text-[11px] text-rose-400">Last sync error: {boxed.error}</div>}
            <button onClick={syncBoxed} disabled={boxedSyncing || boxed?.running} data-testid="boxed-sync-button" className="mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-lg border text-xs text-slate-300 hover:bg-[var(--pk-hover)] disabled:opacity-50" style={{ borderColor: "var(--pk-border)" }}>
              {(boxedSyncing || boxed?.running) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Force sync buylist
            </button>
            <p className="text-[11px] text-slate-600 mt-1.5">Pulls the latest Boxed.gg buylist now so buy offers and payout estimates reflect current prices (otherwise refreshed automatically every 4 hours).</p>
            {boxed?.last_synced && <div className="text-[11px] text-slate-600 mt-2">Updated {new Date(boxed.last_synced).toLocaleString()}</div>}
          </div>
        </div>
      </div>

      {report && (
        <div className="mt-6 rounded-2xl border p-6" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }} data-testid="reprice-report">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-amber-400" />
              <h3 className="font-heading font-bold text-slate-100">Last reprice report</h3>
            </div>
            <span className="text-xs text-slate-500">{report.ran_at ? new Date(report.ran_at).toLocaleString() : ""}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <ReportStat label="Changed" value={report.updated} color="text-emerald-400" />
            <ReportStat label="Unchanged" value={report.unchanged} color="text-slate-300" />
            <ReportStat label="No data" value={report.no_data} color="text-amber-400" />
            <ReportStat label="Shopify updated" value={report.shopify_updated} color="text-cyan-400" />
          </div>
          {report.changes?.length > 0 ? (
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--pk-border)" }}>
              <div className="max-h-80 overflow-y-auto divide-y" style={{ borderColor: "var(--pk-border-subtle)" }}>
                {report.changes.map((ch, i) => (
                  <div key={i} data-testid="reprice-report-row" className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <div className="min-w-0">
                      <div className="text-sm text-slate-100 truncate">{ch.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{ch.set_name || "—"} · {ch.condition}{ch.printing ? ` · ${ch.printing}` : ""} · <span className="uppercase">{ch.source}</span></div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="font-mono-num text-xs text-slate-500">{fmt(ch.old)} → <span className="text-slate-200">{fmt(ch.new)}</span></span>
                      <span className={`inline-flex items-center gap-0.5 font-mono-num text-xs font-semibold w-16 justify-end ${ch.delta >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                        {ch.delta >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}{fmt(Math.abs(ch.delta))}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500">No price changes in the last run.</p>
          )}
          {report.no_data_cards?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-amber-400/90 mb-2">No pricing data ({report.no_data_cards.length})</div>
              <div className="flex flex-wrap gap-1.5">
                {report.no_data_cards.map((n, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-amber-500/10 text-amber-300/90 border border-amber-500/20">{n.name} · {n.condition}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReportStat({ label, value, color }) {
  return (
    <div className="rounded-xl border p-3" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xl font-bold font-mono-num ${color}`}>{value ?? 0}</div>
    </div>
  );
}

function Row({ label, value, color, icon: Icon }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-slate-400 flex items-center gap-1.5">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</span>
      <span className={`font-mono-num font-bold ${color}`}>{value}</span>
    </div>
  );
}
